import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { Activity, AlertTriangle, Gauge, Thermometer, Droplets, Zap, Filter } from 'lucide-react';

interface MetricCard {
  id: string;
  name: string;
  value: number;
  unit: string;
  trend: 'up' | 'down' | 'stable';
  status: 'normal' | 'warning' | 'critical';
  history: Array<{ timestamp: number; value: number }>;
}

export const RealTimeMonitoring: React.FC = () => {
  const [metrics, setMetrics] = useState<MetricCard[]>([
    {
      id: 'pressure',
      name: 'System Pressure',
      value: 4.2,
      unit: 'bar',
      trend: 'stable',
      status: 'normal',
      history: Array(20).fill(0).map((_, i) => ({
        timestamp: Date.now() - (19 - i) * 60000,
        value: 4.2 + Math.random() * 0.4 - 0.2
      }))
    },
    {
      id: 'flow',
      name: 'Flow Rate',
      value: 250,
      unit: 'm³/h',
      trend: 'up',
      status: 'normal',
      history: Array(20).fill(0).map((_, i) => ({
        timestamp: Date.now() - (19 - i) * 60000,
        value: 250 + Math.random() * 20 - 10
      }))
    },
    {
      id: 'conductivity',
      name: 'Conductivity',
      value: 450,
      unit: 'µS/cm',
      trend: 'down',
      status: 'warning',
      history: Array(20).fill(0).map((_, i) => ({
        timestamp: Date.now() - (19 - i) * 60000,
        value: 450 + Math.random() * 40 - 20
      }))
    },
    {
      id: 'temperature',
      name: 'Temperature',
      value: 23.5,
      unit: '°C',
      trend: 'up',
      status: 'normal',
      history: Array(20).fill(0).map((_, i) => ({
        timestamp: Date.now() - (19 - i) * 60000,
        value: 23.5 + Math.random() * 1 - 0.5
      }))
    }
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      setMetrics(prevMetrics => prevMetrics.map(metric => ({
        ...metric,
        value: metric.value + (Math.random() * 2 - 1) * (metric.value * 0.02),
        history: [
          ...metric.history.slice(1),
          {
            timestamp: Date.now(),
            value: metric.value + (Math.random() * 2 - 1) * (metric.value * 0.02)
          }
        ]
      })));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const getMetricIcon = (metricId: string) => {
    switch (metricId) {
      case 'pressure':
        return <Gauge className="h-6 w-6" />;
      case 'flow':
        return <Droplets className="h-6 w-6" />;
      case 'conductivity':
        return <Zap className="h-6 w-6" />;
      case 'temperature':
        return <Thermometer className="h-6 w-6" />;
      default:
        return <Activity className="h-6 w-6" />;
    }
  };

  const getStatusColor = (status: MetricCard['status']) => {
    switch (status) {
      case 'normal':
        return 'text-green-500';
      case 'warning':
        return 'text-yellow-500';
      case 'critical':
        return 'text-red-500';
    }
  };

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map(metric => (
          <div
            key={metric.id}
            className="bg-white rounded-lg shadow-lg p-6 transition-transform hover:scale-105"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                {getMetricIcon(metric.id)}
                <h3 className="text-lg font-semibold text-gray-900">{metric.name}</h3>
              </div>
              <span className={`${getStatusColor(metric.status)}`}>
                <Activity className="h-5 w-5" />
              </span>
            </div>
            <div className="mb-4">
              <div className="flex items-baseline space-x-2">
                <span className="text-3xl font-bold text-gray-900">
                  {metric.value.toFixed(1)}
                </span>
                <span className="text-gray-500">{metric.unit}</span>
              </div>
            </div>
            <div className="h-20">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={metric.history}>
                  <defs>
                    <linearGradient id={`gradient-${metric.id}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#3b82f6"
                    fill={`url(#gradient-${metric.id})`}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        ))}
      </div>

      {/* Detailed Chart */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">System Performance</h3>
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={metrics[0].history}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="timestamp"
                tickFormatter={(timestamp) => new Date(timestamp).toLocaleTimeString()}
              />
              <YAxis />
              <Tooltip
                labelFormatter={(timestamp) => new Date(timestamp).toLocaleString()}
                formatter={(value: number) => value.toFixed(2)}
              />
              {metrics.map(metric => (
                <Line
                  key={metric.id}
                  type="monotone"
                  dataKey="value"
                  name={metric.name}
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* System Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Alerts</h3>
          <div className="space-y-4">
            <div className="flex items-center space-x-3 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              <div>
                <p className="text-sm font-medium text-yellow-800">High Conductivity Warning</p>
                <p className="text-xs text-yellow-600">Exceeding normal operating range</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">System Health</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-green-800">Membrane Status</span>
                <Filter className="h-5 w-5 text-green-500" />
              </div>
              <p className="text-lg font-semibold text-green-900">Optimal</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-800">Energy Efficiency</span>
                <Zap className="h-5 w-5 text-blue-500" />
              </div>
              <p className="text-lg font-semibold text-blue-900">95%</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};